import type { StoreActionApi } from 'react-sweet-state';
import { createStore, createHook, createActionsHook } from 'react-sweet-state';

import type { ContentId, ContentTreeItem } from './contentTreeItemType';

interface ContentTreeSelectionState {
	root: ContentTreeItem | null; // Page tree root
	count: number; // Count represents the number of selected content items and its descendants (if applicable).
	isMultiSelectActive: boolean;
	// Set of content IDs for which descendant counts are currently being loaded.
	loadingDescendantIds: Set<ContentId>;
	selectedContent: Record<
		ContentId,
		{
			selectedDescendantCount: number;
			deselectedChildren: Set<ContentId>;
		}
	>;
}

const initialState: ContentTreeSelectionState = {
	root: null,
	isMultiSelectActive: false,
	selectedContent: {},
	count: 0,
	loadingDescendantIds: new Set(),
};

const actions = {
	enterMultiSelect:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				isMultiSelectActive: true,
				selectedContent: {},
				count: 0,
			});
		},
	exitMultiSelect:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				isMultiSelectActive: false,
				selectedContent: {},
				root: null,
				count: 0,
			});
		},
	setRoot:
		(root: ContentTreeItem, children: ContentId[]) =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			const rootContent = {
				[root.id]: {
					selectedDescendantCount: 0,
					deselectedChildren: new Set<ContentId>(children),
				},
			};

			setState({
				root,
				selectedContent: rootContent,
			});
		},
	selectContent:
		(contentId: ContentId, selectedDescendantCount: number) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();

			// If this is a new selection, add 1 (just the item itself)
			// otherwise, add the number of descendants
			const isNew = !(contentId in selectedContent);
			const delta = isNew ? 1 + selectedDescendantCount : selectedDescendantCount;

			const newSelectedContent = {
				...selectedContent,
				[contentId]: {
					selectedDescendantCount,
					deselectedChildren:
						selectedContent[contentId]?.deselectedChildren ?? new Set<ContentId>(),
				},
			};

			setState({
				selectedContent: newSelectedContent,
				count: getState().count + delta,
			});
		},
	selectNestedContent:
		(contentIds: ContentId[], selectedDescendantCount: number) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();

			const newSelectedContent = { ...selectedContent };
			for (const contentId of contentIds) {
				newSelectedContent[contentId] = {
					selectedDescendantCount,
					deselectedChildren:
						selectedContent[contentId]?.deselectedChildren ?? new Set<ContentId>(),
				};
			}

			setState({
				selectedContent: newSelectedContent,
				count: 0,
			});
		},
	deselectNestedContent:
		(contentIds: ContentId[], parentIds: ContentId[]) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent } = getState();
			let newSelectedContent = { ...selectedContent };
			contentIds.forEach((contentId) => {
				// Remove the contentId from selectedContent
				const { [contentId]: removed, ...rest } = newSelectedContent;
				newSelectedContent = rest;
				// For each parentId, update its deselectedChildren set
				parentIds.forEach((parentId) => {
					if (parentId in newSelectedContent) {
						const parentContent = { ...newSelectedContent[parentId] };
						if (!parentContent.deselectedChildren) {
							parentContent.deselectedChildren = new Set<ContentId>();
						}
						parentContent.deselectedChildren.add(contentId);
						newSelectedContent[parentId] = parentContent;
					}
				});
			});
			setState({
				selectedContent: newSelectedContent,
				count: 0,
			});
		},
	deselectContent:
		(contentId: ContentId, parentId?: ContentId) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const { selectedContent, loadingDescendantIds, count } = getState();

			const { [contentId]: removed, ...rest } = selectedContent;

			if (parentId && parentId in selectedContent) {
				const parentContent = rest[parentId] || { deselectedChildren: new Set<ContentId>() };
				parentContent.deselectedChildren.add(contentId);
				rest[parentId] = parentContent;
			}

			const isLoadingDescendantCount = !loadingDescendantIds.has(contentId);
			const newCount = isLoadingDescendantCount
				? count - 1 - (removed?.selectedDescendantCount || 0)
				: count - 1;

			setState({
				selectedContent: rest,
				count: newCount,
			});
		},
	clearSelection:
		() =>
		({ setState }: StoreActionApi<ContentTreeSelectionState>) => {
			setState({
				selectedContent: {},
				count: 0,
			});
		},
	addLoadingDescendant:
		(contentId: ContentId) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const next = new Set(getState().loadingDescendantIds);
			next.add(contentId);
			setState({ loadingDescendantIds: next });
		},
	removeLoadingDescendant:
		(contentId: ContentId) =>
		({ setState, getState }: StoreActionApi<ContentTreeSelectionState>) => {
			const next = new Set(getState().loadingDescendantIds);
			next.delete(contentId);
			setState({ loadingDescendantIds: next });
		},
};

const Store = createStore<ContentTreeSelectionState, typeof actions>({
	initialState,
	actions,
	name: 'contentTreeSelection',
});

export const useContentTreeSelection = createHook(Store);
export const useContentTreeSelectionActions = createActionsHook(Store);
